import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { Tooltip } from '@/components/core/Tooltip';
import { Uploader, type UploadItem } from '@/components/core/Uploader';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import {
    type BulkSubscriptionImportAction,
    useImportStagedBulkSubscriptionFile,
    useStageBulkSubscriptionFile,
} from '../bulk-subscriptions.queries';
import { BulkSubscription } from '../bulk-subscriptions.types';

// Server-side allow-list for POST /bulkSubscription/{id}/_upload lives in
// BulkSubscriptionResource.java (lines 708 & 795) and only accepts
// MimeType.JSON and MimeType.XLSX. The Figma "Import file" modal at
// https://www.figma.com/design/OI1DqEXIqzYdi41E8cM7ea/Optimize-usability?node-id=5887-29535
// additionally lists `.xlsm`, but that's not implemented on the backend
// yet — adding it here would let the user pick a file that the server
// then rejects. We therefore stay on the two formats the API actually
// accepts and treat `.xlsm` support as a follow-up backend change.
const ACCEPTED_FILE_TYPES = ['.json', '.xlsx'];
// App Engine Standard caps multipart request bodies at 32MB; the Figma
// helper text mirrors that limit. We enforce the same cap client-side so
// users get an inline error instead of a server-side rejection.
const MAX_FILE_SIZE = 32 * 1024 * 1024;

// Map the three Figma radio options to the server's `action` query param
// (see `BulkSubscriptionResource.uploadBulkSubscriptionEntriesForId`).
type ImportOption = {
    action: BulkSubscriptionImportAction;
    labelKey: string;
    tooltipKey: string;
};

const IMPORT_OPTIONS: ImportOption[] = [
    {
        action: 'ADD_ENTRIES',
        labelKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_ADD',
        tooltipKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_ADD_HELP',
    },
    {
        action: 'REPLACE_ENTRIES',
        labelKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_REPLACE',
        tooltipKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_REPLACE_HELP',
    },
    {
        action: 'DELETE_ENTRIES',
        labelKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_DELETE',
        tooltipKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_DELETE_HELP',
    },
];

const DEFAULT_ACTION: BulkSubscriptionImportAction = 'ADD_ENTRIES';

interface UploadEntriesDialogProps {
    entity: BulkSubscription,
    open: boolean;
    onClose: (shouldRefetchData?: boolean) => void;
}

const UploadNewEntriesDialog = ({
    entity,
    open,
    onClose,
}: UploadEntriesDialogProps) => {

    const { toast } = useToast();
    const { t } = useTranslation();

    // The dialog runs a genuine two-step flow:
    //   1. Browse / drag & drop → stream the file to GCS via a signed
    //      upload URL. Progress bar reflects the real PUT.
    //   2. "Start import" click → hand the resulting `assetKey` to the
    //      bulk-subscription resource, which finalizes the digital asset
    //      and enqueues the actual import.
    // `stagedAssetKey` bridges the two steps. `uploadItem` drives the
    // Uploader UI. An `AbortController` lets Cancel / Remove / unmount
    // stop an in-flight GCS upload immediately instead of leaving it to
    // race back with stale state.
    const [stagedFile, setStagedFile] = useState<File | undefined>(undefined);
    const [stagedAssetKey, setStagedAssetKey] = useState<string | undefined>(undefined);
    const [uploadItem, setUploadItem] = useState<UploadItem | undefined>(undefined);
    const [dropFieldError, setDropFieldError] = useState<string | undefined>(undefined);
    const [action, setAction] = useState<BulkSubscriptionImportAction>(DEFAULT_ACTION);

    const stageAbortRef = useRef<AbortController | null>(null);

    const abortInFlightStaging = useCallback(() => {
        if (stageAbortRef.current) {
            stageAbortRef.current.abort();
            stageAbortRef.current = null;
        }
    }, []);

    useEffect(() => () => abortInFlightStaging(), [abortInFlightStaging]);

    const {
        mutate: stageFile,
        isPending: isStaging,
    } = useStageBulkSubscriptionFile({
        onSuccess: ({ assetKey }) => {
            stageAbortRef.current = null;
            setStagedAssetKey(assetKey);
            setUploadItem((prev) =>
                prev ? { ...prev, status: 'success', progress: 100 } : prev
            );
        },
        onError: (error) => {
            stageAbortRef.current = null;
            // A user-triggered abort surfaces as an axios CanceledError. It
            // isn't an upload failure — we've already reset the dialog
            // state elsewhere (Cancel / Remove / new file), so swallow it
            // instead of flipping the row into 'error'.
            if (axios.isCancel(error)) {
                return;
            }
            logger.error('UploadNewEntriesDialog: Could not stage file to GCS', error);
            const errorMessage = resolveUploadErrorMessage(error, t);
            setUploadItem((prev) =>
                prev
                    ? { ...prev, status: 'error', errorMessage }
                    : prev
            );
        },
    });

    const {
        mutate: importStagedFile,
        isPending: isImporting,
    } = useImportStagedBulkSubscriptionFile({
        onSuccess: () => {
            toast.success('BULK_SUBSCRIPTION.NEW_ENTRIES_UPLOADED_SUCCESSFULLY');
            onClose(true);
        },
        onError: (error) => {
            logger.error('UploadNewEntriesDialog: Could not import staged file', error);
            const errorMessage = resolveUploadErrorMessage(error, t);
            setUploadItem((prev) =>
                prev
                    ? { ...prev, status: 'error', errorMessage }
                    : prev
            );
        },
    });

    const runStaging = useCallback((file: File) => {
        abortInFlightStaging();
        const controller = new AbortController();
        stageAbortRef.current = controller;
        setStagedAssetKey(undefined);
        setUploadItem({
            id: file.name,
            file,
            status: 'uploading',
            progress: 0,
            startedAt: Date.now(),
        });
        stageFile({
            file,
            signal: controller.signal,
            onProgress: (percent) => {
                setUploadItem((prev) =>
                    prev && prev.status === 'uploading'
                        ? { ...prev, progress: percent }
                        : prev
                );
            },
        });
    }, [abortInFlightStaging, stageFile]);

    const handleFilesAdded = useCallback((files: File[]) => {
        const [file] = files;
        if (!file) {
            return;
        }
        // Figma Frame 03/02: only one file can be staged at a time. If the
        // user tries to add another file (via drag & drop or via "Browse
        // files") while one is already staged, we must surface an inline
        // error and keep the existing file — silently swapping would risk
        // the user thinking their previous selection is still in play.
        if (stagedFile) {
            setDropFieldError(t('BULK_SUBSCRIPTION.SINGLE_FILE_ONLY_ERROR'));
            return;
        }
        setDropFieldError(undefined);
        setStagedFile(file);
        runStaging(file);
    }, [stagedFile, t, runStaging]);

    const clearStagedState = useCallback(() => {
        abortInFlightStaging();
        setStagedFile(undefined);
        setStagedAssetKey(undefined);
        setUploadItem(undefined);
        setDropFieldError(undefined);
    }, [abortInFlightStaging]);

    const handleFileRemove = useCallback(() => {
        // Removal during the server-side import is blocked — clearing
        // state mid-flight would leave the POST racing back with nothing
        // to update. The trash / X button is hidden in that state anyway;
        // this is just a belt-and-braces guard.
        if (isImporting) {
            return;
        }
        clearStagedState();
    }, [isImporting, clearStagedState]);

    const runImport = useCallback((file: File, assetKey: string) => {
        // Flip the row back to a healthy "ready" state so the user
        // doesn't see a stale error row while the retry POST is in
        // flight. The button's own `loading={isImporting}` spinner is
        // the primary progress affordance for this step (the `_upload`
        // POST has no body worth streaming a progress bar for).
        setUploadItem({
            id: file.name,
            file,
            status: 'success',
            progress: 100,
        });
        importStagedFile({
            entityId: entity.id,
            assetKey,
            action,
        });
    }, [entity.id, action, importStagedFile]);

    const handleFileRetry = useCallback(() => {
        if (!stagedFile) {
            return;
        }
        // If GCS staging already succeeded, only the import step failed
        // — re-run that step alone so we don't re-upload the same bytes.
        // Otherwise, replay the whole flow starting from `/gatherings/uploadurl`.
        if (stagedAssetKey) {
            runImport(stagedFile, stagedAssetKey);
            return;
        }
        runStaging(stagedFile);
    }, [stagedFile, stagedAssetKey, runImport, runStaging]);

    const isReadyToImport =
        !!stagedFile && !!stagedAssetKey && uploadItem?.status === 'success' && !isImporting;

    const onStartImportClick = () => {
        if (isImporting) {
            return;
        }
        // Figma Frame 03/01: clicking "Start import" without a file must
        // surface an inline error under the DropField instead of silently
        // doing nothing.
        if (!stagedFile) {
            setDropFieldError(t('BULK_SUBSCRIPTION.NO_FILE_SELECTED_ERROR'));
            return;
        }
        if (!isReadyToImport || !stagedAssetKey) {
            // File is still being uploaded to GCS, or the upload failed.
            // The button is visually disabled in this state anyway.
            return;
        }
        runImport(stagedFile, stagedAssetKey);
    };

    const discardAndClose = () => {
        // Figma Frame 04: Cancel unstages the file and closes the dialog
        // without triggering a data refetch on the parent screen. Any
        // GCS object already written for `stagedAssetKey` is left to the
        // storage lifecycle to clean up, matching the existing large-file
        // upload flow (`useAssetFolderUploader`).
        clearStagedState();
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={() => !isImporting && discardAndClose()}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault>
                        {t('BULK_SUBSCRIPTION.IMPORT_FILE')}
                    </DialogHeaderDefault>
                </DialogHeader>

                {/*
                  Figma frame `5887:29535 / 01 Import file Dialog` places
                  the Uploader directly under the header and adds a
                  bordered Options block below it. The DialogBody owns no
                  padding — each block ships its own internal padding.
                */}
                <DialogBody padding="none" className="tw-flex tw-flex-col">
                    <Uploader
                        acceptedFileTypes={ACCEPTED_FILE_TYPES}
                        maxFileSize={MAX_FILE_SIZE}
                        uploads={uploadItem ? [uploadItem] : []}
                        isDisabled={isImporting}
                        errorText={dropFieldError}
                        onFilesAdded={handleFilesAdded}
                        onFileRemove={handleFileRemove}
                        onFileRetry={handleFileRetry}
                    />

                    <RadioGroup
                        value={action}
                        onValueChange={(value) => setAction(value as BulkSubscriptionImportAction)}
                        disabled={isImporting}
                        className="tw-flex tw-flex-col tw-gap-1 tw-px-5 tw-py-3 lg:tw-px-8 tw-border-t tw-border-divider"
                    >
                        {IMPORT_OPTIONS.map(option => {
                            const inputId = `bulk-sub-import-option-${option.action}`;
                            return (
                                <div key={option.action} className="tw-flex tw-items-center tw-gap-2">
                                    <RadioGroupItem value={option.action} id={inputId} />
                                    <label
                                        htmlFor={inputId}
                                        className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-primary tw-whitespace-nowrap tw-m-0"
                                    >
                                        {t(option.labelKey)}
                                    </label>
                                    <Tooltip popupContent={t(option.tooltipKey)} size="sm" />
                                </div>
                            );
                        })}
                    </RadioGroup>
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="default"
                        disabled={isImporting || isStaging || !isReadyToImport}
                        loading={isImporting}
                        onClick={onStartImportClick}
                    >
                        {t('BULK_SUBSCRIPTION.START_IMPORT')}
                    </Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" disabled={isImporting} onClick={discardAndClose}>
                            {t('CANCEL')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/**
 * Maps a server error to one of the three Figma-defined bulk-subscription
 * upload error strings (Frame 03/05, 03/06). We keep the exact server
 * message as the fallback because a specific business error (e.g. an
 * invalid workbook layout) is often more informative than the generic
 * "technical error" copy.
 */
function resolveUploadErrorMessage(
    error: unknown,
    t: (key: string, values?: Record<string, string | number>) => string,
): string {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
            return t('BULK_SUBSCRIPTION.MISSING_UPLOAD_RIGHTS');
        }
        const serverMessage = (error.response?.data as { message?: string } | undefined)?.message;
        if (serverMessage) {
            return serverMessage;
        }
    }

    return t('BULK_SUBSCRIPTION.TECHNICAL_ERROR');
}

export { UploadNewEntriesDialog };
